import React, { useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from './supabaseClient';

const BRAND = '#163248';
const ACCENT = '#38b6ff';

const ROLES = ['Виконроб', 'Будівельник', 'Менеджер проєкту', 'Дизайнер', 'Власник компанії', 'Інше'];
const FEATURES = ['Кошторис', 'Шаблони', 'Довідник цін', 'AI-імпорт', 'Таймлайн', 'Замовлення', 'PDF / Excel'];

export default function PatchNoteModal({ isOpen, onClose, user }) {
  const [step, setStep] = useState(1);
  const [rating, setRating] = useState(4);
  const [role, setRole] = useState('');
  const [usefulFeatures, setUsefulFeatures] = useState([]);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    localStorage.setItem('patchnote_sep2026_links_seen', 'true');
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await supabase.from('user_feedback').insert({
        user_id: user?.id,
        rating: rating || null,
        role: role || null,
        useful_features: usefulFeatures.length > 0 ? usefulFeatures : null,
        review: reviewText.trim() || null,
        feedback_type: 'patchnote_sep2026_links',
      });
    } catch (e) {
      console.error('Feedback save error:', e);
    }
    setSubmitting(false);
    setStep(3);
  };

  const handleSkipStep2 = () => {
    // Зберігаємо що юзер бачив але не відповів
    try {
      supabase.from('user_feedback').insert({
        user_id: user?.id,
        rating: null,
        role: null,
        useful_features: null,
        review: null,
        feedback_type: 'patchnote_sep2026_links_skipped',
      });
    } catch (e) { /* ignore */ }
    setStep(3);
  };

  const toggleFeature = (f) => {
    setUsefulFeatures(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);
  };

  // ═══ STEP 1: Що нового ═══
  if (step === 1) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[80]" onClick={handleClose}>
        <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[95vh] sm:max-h-[85vh] shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div className="flex-shrink-0 text-white px-6 pt-6 pb-8 relative" style={{ background: BRAND }}>
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full" style={{ background: 'rgba(56,182,255,0.15)', color: ACCENT }}>Оновлення</span>
              <button onClick={handleClose} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
                <X className="w-4.5 h-4.5" style={{ color: 'rgba(255,255,255,0.5)' }} />
              </button>
            </div>
            <div style={{ fontFamily: "'Archivo Black', sans-serif", fontSize: 24, letterSpacing: 1 }}>LIHTAR</div>
            <div className="text-lg font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>Три оновлення для роботи з клієнтом і між пристроями</div>
            <div className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Вересень 2026</div>
            <div className="absolute bottom-0 left-0 right-0 h-4 bg-white" style={{ borderRadius: '24px 24px 0 0' }} />
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 pb-4">

            <p className="text-[13px] text-gray-500 leading-relaxed mb-5" style={{ fontStyle: 'italic' }}>
              За останній місяць ми зробили три великі оновлення. Разом вони роблять роботу з клієнтом наочнішою, а роботу між вашими пристроями — безпечнішою.
            </p>

            {/* 1 */}
            <div className="mb-5">
              <div className="flex items-baseline gap-2.5 mb-2">
                <span className="text-lg font-bold" style={{ color: BRAND }}>1</span>
                <span className="text-sm font-semibold" style={{ color: BRAND }}>Захист від втрати даних між пристроями</span>
              </div>
              <p className="text-[13px] text-gray-600 leading-relaxed">
                Раніше через одночасне відкриття LIHTAR на комп'ютері й телефоні (або в кількох вкладках) деякі об'єкти зникали. Тепер система розуміє де ви зараз працюєте: <strong>одне вікно стає активним</strong>, інші переходять у пасивний режим і показують попередження. Активне вікно можна перемикати кнопкою у смузі попередження. Ваші дані більше не переписуються між пристроями.
              </p>
            </div>

            {/* 2 */}
            <div className="mb-5">
              <div className="flex items-baseline gap-2.5 mb-2">
                <span className="text-lg font-bold" style={{ color: BRAND }}>2</span>
                <span className="text-sm font-semibold" style={{ color: BRAND }}>Оновлене посилання для клієнта</span>
              </div>
              <p className="text-[13px] text-gray-600 leading-relaxed">
                Клієнти отримали нову сторінку перегляду проекту. Зверху — панель з ключовими цифрами: скільки заплановано, скільки виконано, скільки оплачено. Кругова діаграма показує розподіл витрат по категоріях. Список позицій став чистішим і зрозумілішим. Все автоматично адаптується під телефон.
              </p>
              <p className="text-[13px] text-gray-600 leading-relaxed mt-2">
                Щоб клієнт побачив нову версію — при генерації посилання поставте галочку <strong>«показати попередній кошторис»</strong>. Без галочки клієнту показується попередня версія посилання зі звітами.
              </p>
            </div>

            {/* 3 */}
            <div className="mb-4">
              <div className="flex items-baseline gap-2.5 mb-2">
                <span className="text-lg font-bold" style={{ color: BRAND }}>3</span>
                <span className="text-sm font-semibold" style={{ color: BRAND }}>Прив'язка виконання позицій до звітів</span>
              </div>
              <p className="text-[13px] text-gray-600 leading-relaxed">
                Клієнти часто плуталися: що вже зроблено, за що заплачено, скільки залишилось. Тепер ця картина видна одразу.
              </p>
              <p className="text-[13px] text-gray-600 leading-relaxed mt-2">
                Клік по галочці «виконано» в попередньому кошторисі тепер відкриває модалку. Ви вибираєте з якого звіту виконана позиція, з якого розділу, яка саме позиція. Плюс вказуєте: робота <strong>повністю завершена</strong> або <strong>буде продовжена</strong> в наступних звітах.
              </p>
              <p className="text-[13px] text-gray-600 leading-relaxed mt-2">
                На клієнтському посиланні біля кожної виконаної позиції з'являється <strong style={{ color: '#22c55e' }}>зелена крапка</strong> (повне виконання) або <strong>напівзелена</strong> (часткове). <strong style={{ color: '#f97316' }}>Оранжева крапка</strong> означає що звіт з цією позицією вже оплачений. Система рахує оплати автоматично: розподіляє платежі клієнта по звітах у порядку створення.
              </p>
              <p className="text-[13px] text-gray-600 leading-relaxed mt-2">
                Коли часткові виконання складаються до повної суми — галочка автоматично стає повністю залитою. Виконроб не має слідкувати за арифметикою вручну.
              </p>
            </div>

            {/* Legacy notice */}
            <div className="p-3.5 rounded-xl mb-2" style={{ background: '#fef3c7', border: '1px solid #fde68a' }}>
              <p className="text-[12px] text-gray-700 leading-relaxed">
                <span className="font-semibold" style={{ color: '#92400e' }}>Про старі позначки:</span> позиції які ви раніше вже позначали як виконані — залишились такими для клієнта. Але щоб клієнт побачив ще й статус оплати, клікніть по галочці ще раз і прив'яжіть позицію до звіту через модалку. Якщо просто хочете залишити позначку без прив'язки — натисніть <strong>«Готово»</strong> не заповнюючи форму. Тоді біля позиції з'явиться <strong style={{ color: '#dc2626' }}>червоний знак оклика</strong> — це нагадування що позиція виконана без деталізації.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 px-6 py-4 border-t bg-white">
            <div className="flex justify-center gap-1.5 mb-3">
              <div className="w-2 h-2 rounded-full" style={{ background: BRAND }} />
              <div className="w-2 h-2 rounded-full bg-gray-200" />
              <div className="w-2 h-2 rounded-full bg-gray-200" />
            </div>
            <button onClick={() => setStep(2)} className="w-full py-2.5 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition-opacity" style={{ background: BRAND }}>Далі</button>
            <button onClick={() => setStep(2)} className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors mt-1">Пропустити</button>
          </div>
        </div>
      </div>
    );
  }

  // ═══ STEP 2: Оцінка ═══
  if (step === 2) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[80]" onClick={handleClose}>
        <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[95vh] sm:max-h-[85vh] shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>

          <div className="flex-1 overflow-y-auto px-6 pt-6 pb-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold" style={{ color: BRAND }}>Як вам LIHTAR?</h2>
              <button onClick={handleClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Stars */}
            <div className="flex items-center gap-1 mb-6">
              {[1,2,3,4,5].map(n => (
                <button key={n} onClick={() => setRating(n)} className="text-[28px] transition-transform hover:scale-110" style={{ color: n <= rating ? '#f59e0b' : '#d1d5db', cursor: 'pointer' }}>★</button>
              ))}
              <span className="text-xs text-gray-400 ml-2">{rating} з 5</span>
            </div>

            {/* Role */}
            <div className="mb-5">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Ваша роль</div>
              <div className="flex flex-wrap gap-2">
                {ROLES.map(r => (
                  <button key={r} onClick={() => setRole(r)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all"
                    style={role === r ? { background: BRAND, color: '#fff', borderColor: BRAND } : { background: '#fff', color: '#4b5563', borderColor: '#e5e7eb' }}
                  >{r}</button>
                ))}
              </div>
            </div>

            {/* Features */}
            <div className="mb-5">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Що найкорисніше?</div>
              <div className="text-[11px] text-gray-400 mb-2">можна кілька</div>
              <div className="flex flex-wrap gap-2">
                {FEATURES.map(f => (
                  <button key={f} onClick={() => toggleFeature(f)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all"
                    style={usefulFeatures.includes(f) ? { background: BRAND, color: '#fff', borderColor: BRAND } : { background: '#fff', color: '#4b5563', borderColor: '#e5e7eb' }}
                  >{f}</button>
                ))}
              </div>
            </div>

            {/* Review */}
            <div className="mb-2">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Ваші враження</div>
                <div className="text-[11px] text-gray-400">необов'язково</div>
              </div>
              <textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value.slice(0, 500))}
                placeholder="Що подобається? Чого не вистачає? Будь-яка думка нам допоможе..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                rows={3}
                style={{ fontFamily: 'inherit' }}
              />
              <div className="text-right text-[11px] text-gray-300 mt-0.5">{reviewText.length}/500</div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 px-6 py-4 border-t bg-white">
            <div className="flex justify-center gap-1.5 mb-3">
              <div className="w-2 h-2 rounded-full bg-gray-200" />
              <div className="w-2 h-2 rounded-full" style={{ background: BRAND }} />
              <div className="w-2 h-2 rounded-full bg-gray-200" />
            </div>
            <button onClick={handleSubmit} disabled={submitting}
              className="w-full py-2.5 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ background: BRAND }}
            >{submitting ? 'Надсилаю...' : 'Надіслати'}</button>
            <button onClick={handleSkipStep2} className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors mt-1">Пропустити</button>
          </div>
        </div>
      </div>
    );
  }

  // ═══ STEP 3: Дякуємо ═══
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[80]" onClick={handleClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[95vh] sm:max-h-[85vh] shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>

        <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 text-center">
          <div className="text-5xl mb-5">🙌</div>
          <h2 className="text-xl font-bold mb-3" style={{ color: BRAND }}>Дякуємо!</h2>
          <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
            Ваша думка допомагає нам робити LIHTAR кращим для кожного будівельника. Попереду ще багато нового — залишайтесь з нами.
          </p>
        </div>

        <div className="flex-shrink-0 px-6 py-4 border-t bg-white">
          <div className="flex justify-center gap-1.5 mb-3">
            <div className="w-2 h-2 rounded-full bg-gray-200" />
            <div className="w-2 h-2 rounded-full bg-gray-200" />
            <div className="w-2 h-2 rounded-full" style={{ background: BRAND }} />
          </div>
          <button onClick={handleClose} className="w-full py-2.5 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition-opacity" style={{ background: BRAND }}>
            Почати роботу
          </button>
        </div>
      </div>
    </div>
  );
}
